// frontend/src/hooks/useRoles.ts — the roles read surface (list · detail),
// mirroring useUsers. Project-scoped server state (FRONTEND-PATTERNS §State): keys
// are `['roles', projectId, …]` so a mutation's invalidate(['roles', projectId])
// refreshes list + detail + the assignable picker (useProjectRoles) together.
//
// The list endpoint is `users:manage`-gated (admins: all; non-admin holder: their
// managed subset — backend roles.service §listRoles); the detail adds the grant +
// delegation keys. 401 is the centralized apiClient's job (ADR-003); these let the
// error bubble so the page's 5-state contract / form shell renders it.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  RoleDetail,
  RoleListItem,
  RolesListResponse,
} from '@ems-app/schemas/roles/responses';
import { apiClient } from '../lib/apiClient.js';
import type { SmallSource } from './useOptions.js';

export const rolesKeys = {
  /** List key — also the invalidation PREFIX (covers detail + the picker). */
  all: (projectId: string) => ['roles', projectId] as const,
  detail: (projectId: string, roleId: number) => ['roles', projectId, roleId] as const,
};

// ── list ────────────────────────────────────────────────────────────────────
async function fetchRoles(projectId: string): Promise<RolesListResponse> {
  const r = await apiClient.get<RolesListResponse>(`/projects/${projectId}/roles`);
  return r.data;
}

// Module-level selector (referentially stable, §State select doctrine) — the admin
// list shows ALL roles (incl. disabled), unlike useProjectRoles which drops them.
const selectItems = (r: RolesListResponse): RoleListItem[] => r.items;

export function useRoles(projectId: string): UseQueryResult<RoleListItem[], Error> {
  return useQuery({
    queryKey: rolesKeys.all(projectId),
    queryFn: () => fetchRoles(projectId),
    enabled: projectId.length > 0,
    select: selectItems,
  });
}

/** The roles as a picker's source (ADR-080 §3 — the small kind): the WHOLE list
 *  on the press, sharing this hook's own list key; a disabled role is an
 *  inactive row (a holder's current role always shows, marked). */
export function rolesSource(projectId: string): SmallSource<RolesListResponse> {
  return {
    kind: 'small',
    queryKey: rolesKeys.all(projectId),
    fetch: () => fetchRoles(projectId),
    rows: (r) => r.items.map((role) => ({ id: String(role.id), label: role.name, description: role.description ?? undefined, pickable: true, inactive: role.is_disabled, group: null })),
  };
}

// ── detail ────────────────────────────────────────────────────────────────────
/** The role's detail read. `quiet` — a form's Load the latest: a role gone meanwhile answers the form, never the
 *  not-found page (ADR-084 §6). */
export async function fetchRole(projectId: string, roleId: number, quiet = false): Promise<RoleDetail> {
  const r = await apiClient.get<RoleDetail>(`/projects/${projectId}/roles/${roleId}`, quiet ? { quietNotFound: true } : undefined);
  return r.data;
}

export function useRole(
  projectId: string,
  roleId: number | null,
): UseQueryResult<RoleDetail, Error> {
  return useQuery({
    queryKey: rolesKeys.detail(projectId, roleId ?? 0),
    queryFn: () => fetchRole(projectId, roleId as number),
    enabled: projectId.length > 0 && roleId != null && roleId > 0,
  });
}
